import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { _delete, _get, _put } from "../../../api/chatbotApiClient";
import { API_STATUS } from "../sliceUtil";
import store from "../../../app/store";
import { HttpStatusCode } from "axios";
import { CustomError } from "../../../api/CustomError";
import { CustomSuccess } from "../../../api/CustomSuccess";

const THREAD_UPDATE_SUCCESS_MESSAGE = "Thread Updated successfully.";
const THREAD_UPDATE_ERROR_MESSAGE = "Failed to update Thread!";

const dispatchThreadListRefresh = (dispatch, filters, errorMessage) => {
  Promise.resolve(dispatch(fetchThreadList(filters))).catch((error) => {
    console.error(errorMessage, error);
  });
};

export const _orgClientId = () => {
  const state = store.getState();
  return state?.orgReducer?.selectedFI?.orgClientId
    ? state?.orgReducer?.selectedFI?.orgClientId
    : state?.authReducer?.userData?.clientId;
};

const initialState = {
  history: [],
  bookmark: [],
  deleted: [],
  loading: {
    history: API_STATUS.STATUS_IDLE,
    bookmark: API_STATUS.STATUS_IDLE,
    deleted: API_STATUS.STATUS_IDLE,
  },
};

const getType = (arg = {}) => {
  if (arg.isBookMark) return "bookmark";
  if (arg.isDeletedHistory) return "deleted";
  return "history";
};

export const fetchThreadList = createAsyncThunk(
  "chatbot/fetchThreadList",
  async (
    { isBookMark = false, isDeletedHistory = false, disableToast = false } = {},
    thunkAPI,
  ) => {
    
    try {
      const config = {
        successMessage: "Fetched thread list successfully.",
        errorMessage: "Failed to fetch thread list!",
        disableToast,
      };

      const response = await _get(
        `${_orgClientId()}/chat?isBookMark=${isBookMark}&isDeletedHistory=${isDeletedHistory}`,
        { metadata: { apiName: "fetchThreadList" } },
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const {
        data: threadsList,
        error,
        message,
        status,
        statusCode,
        responseType,
      } = CustomSuccess(response, {
        ...config,
        disableToast: true,
      });

      const type = getType({ isBookMark, isDeletedHistory });

      return {
        threadsList,
        error,
        message,
        status,
        statusCode,
        responseType,
        type,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const addOrRemoveBookmark = createAsyncThunk(
  "chatbot/addOrRemoveBookmark",
  async ({ threadDetails, disableToast = false }, thunkAPI) => {
    try {
      const config = {
        successMessage: THREAD_UPDATE_SUCCESS_MESSAGE,
        errorMessage: THREAD_UPDATE_ERROR_MESSAGE,
        disableToast,
      };

      const response = await _put(
        `${_orgClientId()}/chat/bookmark?id=${threadDetails?.threadId}&isStore=${threadDetails?.isBookmark}`,
        {},
        { metadata: { apiName: "addOrRemoveBookmark" } },
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      dispatchThreadListRefresh(
        thunkAPI.dispatch,
        { isBookMark: true, isDeletedHistory: false },
        "Failed to refresh bookmarked threads:",
      );
      dispatchThreadListRefresh(
        thunkAPI.dispatch,
        { isBookMark: false, isDeletedHistory: false },
        "Failed to refresh threads:",
      );

      const { error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });

      return {
        response,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const softdeleteOrRecoverThread = createAsyncThunk(
  "chatbot/softdeleteOrRecoverThread",
  async ({ threadDetails, isDelete, disableToast = false }, thunkAPI) => {
    try {
      const config = {
        successMessage: THREAD_UPDATE_SUCCESS_MESSAGE,
        errorMessage: THREAD_UPDATE_ERROR_MESSAGE,
        disableToast,
      };

      const response = await _delete(
        `${_orgClientId()}/chat/${threadDetails?.threadId}?isDelete=${isDelete}`,
        { metadata: { apiName: "softdeleteOrRecoverThread" } },
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      dispatchThreadListRefresh(
        thunkAPI.dispatch,
        { isBookMark: true, isDeletedHistory: false },
        "Failed to refresh bookmarked threads:",
      );
      dispatchThreadListRefresh(
        thunkAPI.dispatch,
        { isBookMark: false, isDeletedHistory: true },
        "Failed to refresh deleted threads:",
      );
      dispatchThreadListRefresh(
        thunkAPI.dispatch,
        { isBookMark: false, isDeletedHistory: false },
        "Failed to refresh threads:",
      );

      const { error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });

      return {
        response,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const hardDeleteOrEmptyBin = createAsyncThunk(
  "chatbot/hardDeleteOrEmptyBin",
  async ({ threadDetails, isAllDelete, disableToast = false }, thunkAPI) => {
    try {
      const config = {
        successMessage: THREAD_UPDATE_SUCCESS_MESSAGE,
        errorMessage: THREAD_UPDATE_ERROR_MESSAGE,
        disableToast,
      };

      const response = await _delete(
        `${_orgClientId()}/chat?id=${threadDetails?.threadId}&isAllDelete=${isAllDelete}`,
        { metadata: { apiName: "hardDeleteOrEmptyBin" } },
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      dispatchThreadListRefresh(
        thunkAPI.dispatch,
        { isBookMark: false, isDeletedHistory: true },
        "Failed to refresh deleted threads:",
      );

      const { error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });

      return {
        response,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const renameThreadName = createAsyncThunk(
  "chatbot/renameThreadName",
  async ({ threadDetails, disableToast = false }, thunkAPI) => {
    try {
      const config = {
        successMessage: "Thread name Updated successfully.",
        errorMessage: "Failed to update Thread name!",
        disableToast: disableToast,
      };

      const response = await _put(
        `${_orgClientId()}/chat/${threadDetails?.threadId}`,
        {
          threadName: threadDetails?.threadName,
        },
        { metadata: { apiName: "renameThreadName" } },
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      dispatchThreadListRefresh(
        thunkAPI.dispatch,
        { isBookMark: false, isDeletedHistory: false },
        "Failed to refresh threads:",
      );
      dispatchThreadListRefresh(
        thunkAPI.dispatch,
        { isBookMark: true, isDeletedHistory: false },
        "Failed to refresh bookmarked threads:",
      );

      const { error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });

      return {
        response,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const chatbotSidebarToolsSlice = createSlice({
  name: "chatbotSidebar",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchThreadList.pending, (state, action) => {
        const key = getType(action.meta?.arg);

        if (!state.loading) state.loading = {};
        if (!state.loading[key]) state.loading[key] = API_STATUS.STATUS_LOADING;

        state.loading[key] = API_STATUS.STATUS_LOADING;
      })

      .addCase(fetchThreadList.fulfilled, (state, action) => {
        const key = getType(action.meta.arg);

        const list = action.payload?.threadsList;

        const getSafeArray = (list) => {
          if (Array.isArray(list)) return list;
          if (Array.isArray(list?.data)) return list.data;
          return [];
        };

        const safeList = getSafeArray(list);

        state[key] = safeList.map((item) => ({
          ...item,
          type: key,
        }));

        state.loading[key] = API_STATUS.STATUS_SUCCESS;
      })

      .addCase(fetchThreadList.rejected, (state, action) => {
        const key = getType(action.meta?.arg);

        if (!state.loading) state.loading = {};
        state.loading[key] = API_STATUS.STATUS_ERROR;
      });
  },
});
